'use client'

import { useCallback, useState } from 'react'
import { createContext } from '../../utils/context.ts'
import type { MultiselectOptionItem, UseMultiselectProps } from './types.ts'

const initialOptionCount = 100

const isItemVisible = (item: MultiselectOptionItem, search?: string) =>
  search ? item.name?.toLowerCase().includes(search?.toLowerCase()) : true

const defaultPlusMoreText = (count: number) => `+${count} more`

function useMultiselect({
  getPlusMoreText = defaultPlusMoreText,
  isPopout = false,
  onChange,
  onClose,
  options: optionsProp,
  selectedItems = [],
}: UseMultiselectProps) {
  const [search, setSearch] = useState('')
  const [options, setOptions] = useState<MultiselectOptionItem[]>(
    optionsProp.slice(0, initialOptionCount)
  )

  const handleRemove = useCallback(
    (itemToRemove: MultiselectOptionItem) => {
      onChange(
        selectedItems?.filter(
          (selectedItem) => selectedItem.id !== itemToRemove.id
        ) ?? []
      )
    },
    [onChange, selectedItems]
  )

  const handleSearch = useCallback(
    (search: string) => {
      setSearch(search)
      setOptions(optionsProp.filter((option) => isItemVisible(option, search)))
    },
    [optionsProp]
  )

  const handleLoadRemaining = useCallback(() => {
    setOptions(optionsProp)
  }, [optionsProp])

  return {
    allRows: optionsProp,
    getPlusMoreText,
    initialOptionCount,
    isItemVisible,
    isPopout,
    onChange,
    onClose,
    onLoadRemaining: handleLoadRemaining,
    onRemove: handleRemove,
    onSearch: handleSearch,
    options,
    search,
    selectedItems,
  }
}

const [MultiselectProvider, useMultiselectContext] = createContext<
  ReturnType<typeof useMultiselect>
>({
  name: 'MultiselectContext',
})

export {
  MultiselectProvider,
  useMultiselect,
  useMultiselectContext,
  type MultiselectOptionItem,
  type UseMultiselectProps,
}
